import React, { useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdContentCopy, MdInfoOutline } from 'react-icons/md';
import { Button, IconButton, TagPill } from '@aibuild/ui';
import type { AppCatalogEntry, AppManifest } from '@api/apps';
import { FeatureGate, useFeatureGate } from '@featureFlags/index';
import { FullscreenButton } from '../appFrame/FullscreenArea';
import type { AppRunController } from '../appFrame/useAppRunController';
import type { FullscreenControls } from '../appFrame/useFullscreen';
import { canDuplicate } from '../appPermissions';
import { localized } from '../appRunView';
import AppActionsMenu from '../builder/AppActionsMenu';
import RunnerTag from '../runSetup/RunnerTag';
import { useDuplicateAndOpen } from '../useDuplicateAndOpen';
import AppInfoDialog from './AppInfoDialog';
import { useRunMenuActions } from './useRunMenuActions';
import AppLogo from './AppLogo';
import { BarActions, IdentityTags, PageTitle, SrOnly, TitleGroup } from './AppRunHeader.styled';

const statusOf = (intl: IntlShape, controller: AppRunController): string => {
  if (controller.stopping) return intl.formatMessage({ id: 'apps.run.status.stopping', defaultMessage: 'Stopping this run…' });
  if (controller.isBusy) return intl.formatMessage({ id: 'apps.run.working', defaultMessage: 'Working…' });
  return controller.run?.status === 'WAITING_ON_USER'
    ? intl.formatMessage({ id: 'apps.run.status.waiting', defaultMessage: 'Waiting for your answer.' })
    : '';
};

interface AppRunHeaderProps {
  readonly controller: AppRunController;
  readonly manifest: AppManifest;
  readonly catalogEntry: AppCatalogEntry | null;
  readonly fullscreen?: FullscreenControls;
}

// The app's bar is the host's, outside the frame, so a page can never pass itself off as another app.
const AppRunHeader: React.FC<AppRunHeaderProps> = ({ controller, manifest, catalogEntry, fullscreen }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [infoOpen, setInfoOpen] = useState(false);
  const actions = useRunMenuActions(controller, catalogEntry);
  const builderEnabled = useFeatureGate(FeatureGate.OS_APP_BUILDER);
  const { duplicateAndOpen, isDuplicating } = useDuplicateAndOpen();
  const { run } = controller;
  const appKey = catalogEntry?.appKey;
  const duplicable = builderEnabled && appKey !== undefined && canDuplicate(catalogEntry ?? {});
  const infoLabel = intl.formatMessage({ id: 'apps.run.info', defaultMessage: 'About this app' });
  const backLabel = intl.formatMessage({ id: 'apps.builder.back', defaultMessage: 'Back to apps' });

  return (
    <>
      <IconButton
        ariaLabel={backLabel}
        tooltip={backLabel}
        icon={<MdArrowBack size={20} />}
        variant="ghost"
        onClick={() => navigate('/apps')}
        dataTestId="app-run__back"
      />
      <TitleGroup>
        <AppLogo manifest={manifest} dataTestId="app-run__logo" />
        <PageTitle data-testid="app-run__title">{controller.title ?? localized(intl, manifest.name)}</PageTitle>
        <IconButton
          ariaLabel={infoLabel}
          tooltip={infoLabel}
          icon={<MdInfoOutline size={20} />}
          variant="ghost"
          onClick={() => setInfoOpen(true)}
          dataTestId="app-run__info"
        />
      </TitleGroup>
      <IdentityTags>
        {run?.readOnly && (
          <TagPill
            label={intl.formatMessage({ id: 'apps.run.viewOnly', defaultMessage: 'View only' })}
            prefix={false}
            size="sm"
            dataTestId="app-run__view-only"
          />
        )}
        {run?.isTest && (
          <TagPill
            label={intl.formatMessage({ id: 'apps.run.testTag', defaultMessage: 'Test run' })}
            prefix={false}
            size="sm"
            dataTestId="app-run__test-tag"
          />
        )}
        {controller.crossOrg && (
          <TagPill
            label={intl.formatMessage({ id: 'apps.run.viaLinkTag', defaultMessage: 'Shared via link' })}
            prefix={false}
            size="sm"
            dataTestId="app-run__via-link-tag"
          />
        )}
        <RunnerTag controller={controller} />
      </IdentityTags>
      <BarActions>
        {appKey !== undefined && duplicable && (
          <Button
            variant="tonal"
            size="sm"
            leadingIcon={<MdContentCopy size={16} />}
            loading={isDuplicating}
            onClick={() => duplicateAndOpen(appKey)}
            dataTestId="app-run__duplicate"
          >
            {intl.formatMessage({ id: 'apps.run.duplicate', defaultMessage: 'Duplicate' })}
          </Button>
        )}
        <AppActionsMenu
          inline
          ariaLabel={intl.formatMessage({ id: 'apps.run.menu', defaultMessage: 'Run options' })}
          actions={actions}
          dataTestId="app-run__menu"
        />
        {fullscreen && <FullscreenButton fullscreen={fullscreen} />}
      </BarActions>
      <SrOnly role="status" aria-live="polite" data-testid="app-run__status">
        {statusOf(intl, controller)}
      </SrOnly>
      <AppInfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} manifest={manifest} catalogEntry={catalogEntry} />
    </>
  );
};

export default AppRunHeader;
